import { hasServiceWorker } from './capabilities'

export type ServiceWorkerRegisterOutcome = 'registered' | 'unsupported' | 'failed'

export interface ServiceWorkerRegisterResult {
  outcome: ServiceWorkerRegisterOutcome
  registration: ServiceWorkerRegistration | null
  /** Só em `failed`: a mensagem que a plataforma levantou. */
  error?: string
}

/**
 * Singleton de módulo, e não contexto React, porque quem lê é o dispatcher de
 * notificação — que roda dentro de handler de socket, fora de qualquer árvore.
 * `null` enquanto o registro não terminou: o dispatcher devolve `no-registration`
 * nesse intervalo, o que é honesto e observável.
 */
let registrationAtual: ServiceWorkerRegistration | null = null

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return registrationAtual
}

/**
 * A registration, esperando o registro terminar se preciso.
 *
 * **Por que existe:** o singleton só é preenchido quando o `register()` do mount
 * volta, e há uma janela entre o carregamento da página e esse retorno. Quem
 * lesse o singleton nessa janela concluía "não registrado" — e não era verdade:
 * numa RECARGA o worker já está instalado e ativo desde antes de a página
 * existir. Na bancada de 2026-09-04 isso apareceu dos dois lados: o painel de
 * diagnóstico dizia "não registrado nesta sessão" logo após recarregar, e
 * mensagem que chegasse nesse intervalo virava `no-registration` — notificação
 * do SO perdida, com o worker de pé o tempo todo.
 *
 * `navigator.serviceWorker.ready` resolve quando há um worker ATIVO controlando
 * a página; numa recarga é quase imediato. Ele **nunca rejeita e nunca resolve**
 * se não houver registro nenhum, por isso o teto de tempo: sem ele o aviso
 * ficaria pendurado em silêncio, que é o oposto de I3.
 */
export async function awaitServiceWorkerRegistration(
  timeoutMs = 3000,
): Promise<ServiceWorkerRegistration | null> {
  if (registrationAtual) return registrationAtual
  if (!hasServiceWorker()) return null

  let temporizador: ReturnType<typeof setTimeout> | undefined
  const desistir = new Promise<null>((resolve) => {
    temporizador = setTimeout(() => resolve(null), timeoutMs)
  })

  try {
    const registration = await Promise.race([navigator.serviceWorker.ready, desistir])
    if (registration) registrationAtual = registration
    return registration ?? null
  } catch {
    // `ready` não rejeita por contrato, mas um stub de teste ou um browser
    // exótico pode; tratar como ausência é mais honesto que estourar.
    return null
  } finally {
    clearTimeout(temporizador)
  }
}

/** Só para teste — zera o singleton entre casos. */
export function __resetServiceWorkerRegistration(): void {
  registrationAtual = null
}

/**
 * Registra `/sw.js` no escopo raiz.
 *
 * `updateViaCache: 'none'` faz o browser ignorar o HTTP cache ao buscar o script
 * do worker; o `Cache-Control` de `next.config.ts` cuida do outro lado da mesma
 * moeda. Escopo `/` explícito: o script mora na raiz, então esse já é o máximo
 * permitido sem header `Service-Worker-Allowed` (P9), mas declarar deixa a
 * intenção legível.
 *
 * Falha NUNCA é engolida: vira `outcome: 'failed'` com a mensagem. Sem o motivo
 * escrito, "o registro falhou" é indistinguível de "a plataforma não suporta".
 * O caso de produção que já existiu foi `/sw.js` cair num redirect do guard do middleware (desde a faixa 25 o matcher é só `/`):
 * ali a mensagem é de REDE (o *Update* da spec põe redirect mode `error` antes
 * do fetch, então o 307 nunca vira resposta), não o "invalid MIME type" que se
 * costuma procurar.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegisterResult> {
  if (!hasServiceWorker()) {
    registrationAtual = null
    return { outcome: 'unsupported', registration: null }
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    })
    registrationAtual = registration
    return { outcome: 'registered', registration }
  } catch (error) {
    registrationAtual = null
    return {
      outcome: 'failed',
      registration: null,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
